'use client';

import type { ComponentType, ReactNode } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { BodyDefault, LabelDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';
import { HelpCircleIcon } from '@terra/ui-icons';
import { DrawerCollapsible } from './DrawerCollapsible';
import type { DrawerCollapsibleVariant } from './DrawerCollapsible';

export type DetailRow = {
  title: string;
  hint?: string;
  value: React.ReactNode | string;
  withTooltip?: boolean;
  fullWidth?: boolean;
};

export type DrawerGeneralDetailsSize = 'sm' | 'md';

export type DrawerGeneralDetailsJustifyContent = 'start' | 'space-between';

export type DrawerGeneralDetailsProps = {
  title: string;
  rows: DetailRow[][];
  onViewAllClick?: () => void;
  variant?: DrawerCollapsibleVariant;
  layout?: 'grid' | 'list';
  labelWidth?: string;
  dense?: boolean;
  size?: DrawerGeneralDetailsSize;
  justifyContent?: DrawerGeneralDetailsJustifyContent;
};

type DetailFieldTypographyProps = {
  children?: ReactNode;
  color?: string;
  fontSize?: string;
  truncate?: boolean;
  overflow?: string;
  textOverflow?: string;
  whiteSpace?: string;
  display?: string;
};

type DrawerGeneralDetailsSizeConfig = {
  rowsGap: number;
  fieldGap: number;
  rowHeight?: string;
  Label: ComponentType<DetailFieldTypographyProps>;
  Value: ComponentType<DetailFieldTypographyProps>;
  truncateValue?: boolean;
  resolveFontSize?: (
    variant?: DrawerCollapsibleVariant,
  ) => DetailFieldTypographyProps['fontSize'];
};

const DRAWER_GENERAL_DETAILS_SIZE_CONFIG: Record<
  DrawerGeneralDetailsSize,
  DrawerGeneralDetailsSizeConfig
> = {
  sm: {
    rowsGap: 0.5,
    fieldGap: 4,
    rowHeight: '32px',
    Label: LabelDefault,
    Value: BodyDefault,
    truncateValue: true,
  },
  md: {
    rowsGap: 3,
    fieldGap: 5,
    Label: Text,
    Value: Text,
    resolveFontSize: (variant) => (variant === 'box' ? 'sm' : 'md'),
  },
};

type DetailFieldProps = {
  title: string;
  hint?: string;
  value?: React.ReactNode | string;
  withTooltip?: boolean;
  variant?: DrawerCollapsibleVariant;
  labelWidth?: string;
  sizeConfig: DrawerGeneralDetailsSizeConfig;
  justifyContent: DrawerGeneralDetailsJustifyContent;
};

function DetailField({
  title,
  hint,
  value,
  withTooltip = false,
  variant,
  labelWidth,
  sizeConfig,
  justifyContent,
}: DetailFieldProps) {
  const { Label, Value, fieldGap, rowHeight, truncateValue, resolveFontSize } =
    sizeConfig;
  const isSpaceBetween = justifyContent === 'space-between';
  const isStringValue = typeof value === 'string' || typeof value === 'number';
  const fontSize = resolveFontSize?.(variant);
  const displayValue = value ?? '';
  const ellipsisProps = withTooltip
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }
    : undefined;

  const stringContent = (
    <Value
      color="fg.default"
      fontSize={fontSize}
      truncate={truncateValue}
      {...ellipsisProps}
    >
      {displayValue}
    </Value>
  );

  const content = withTooltip ? (
    <Tooltip title={String(value) || ''}>{stringContent}</Tooltip>
  ) : isStringValue ? (
    stringContent
  ) : (
    value
  );

  return (
    <HStack
      gap={fieldGap}
      alignItems="center"
      justifyContent={justifyContent}
      flex={1}
      w="full"
      minH={rowHeight}
      minW={withTooltip ? 0 : undefined}
    >
      <Box
        flex={labelWidth || isSpaceBetween ? 'none' : 1}
        flexShrink={0}
        style={labelWidth ? { width: labelWidth } : undefined}
      >
        <HStack gap={1} alignItems="center">
          <Label color="fg.muted" fontSize={fontSize}>
            {title}
          </Label>
          {hint ? (
            <Tooltip title={hint} positioning={{ strategy: 'fixed' }}>
              <Box display="flex" color="fg.muted">
                <HelpCircleIcon size={14} />
              </Box>
            </Tooltip>
          ) : null}
        </HStack>
      </Box>
      <Box
        flex={1}
        minW={0}
        display="flex"
        justifyContent={isSpaceBetween ? 'flex-end' : undefined}
        textAlign={isSpaceBetween ? 'right' : undefined}
      >
        {content}
      </Box>
    </HStack>
  );
}

export function DrawerGeneralDetails({
  title,
  rows,
  onViewAllClick,
  variant,
  layout = 'grid',
  labelWidth,
  dense,
  size = 'md',
  justifyContent = 'start',
}: DrawerGeneralDetailsProps) {
  const sizeConfig = DRAWER_GENERAL_DETAILS_SIZE_CONFIG[size];

  return (
    <DrawerCollapsible
      title={title}
      onViewAllClick={onViewAllClick}
      variant={variant}
      dense={dense}
    >
      <VStack gap={sizeConfig.rowsGap} alignItems="stretch" w="full">
        {rows.map((row, rowIndex) => (
          <HStack key={rowIndex} gap={12} alignItems="center" w="full">
            {row.map((field, fieldIndex) => (
              <DetailField
                key={fieldIndex}
                title={field.title}
                hint={field.hint}
                value={field.value}
                withTooltip={field.withTooltip}
                variant={variant}
                labelWidth={labelWidth}
                sizeConfig={sizeConfig}
                justifyContent={justifyContent}
              />
            ))}
            {layout === 'grid' && row.length === 1 && !row[0]?.fullWidth && (
              <HStack
                gap={sizeConfig.fieldGap}
                alignItems="center"
                flex={1}
                w="full"
              />
            )}
          </HStack>
        ))}
      </VStack>
    </DrawerCollapsible>
  );
}
